/**
 * Median Filter — denoising via per-channel median of a square window.
 *
 * Replaces each pixel with the median of its (2r+1)² neighbours in
 * each colour channel independently. Strong against salt-and-pepper
 * impulse noise (outlier pixels never become the median); preserves
 * edges better than a Gaussian blur (a step edge stays a step edge —
 * each half just gets its median = its constant value).
 *
 * Two implementations:
 *   • r ≤ 3 → per-pixel sort of K = (2r+1)² ≤ 49 values per channel.
 *     Insertion sort, O(K²) per pixel = O(2401) max — still fast in
 *     practice; the JIT inlines it.
 *   • r > 3 → histogram-median per Huang, Yang & Tang 1979. The
 *     histogram slides one column at a time, O(W) per row pixel
 *     instead of O(K²) — a ~10× speed-up at r = 7.
 *
 * For BLRRD's slider range [1, 7] both paths get exercised. The
 * Huang path is correctness-critical for r ≥ 4 since the brute-force
 * sort blows the perf budget there.
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * radius ≤ 0 → no-op.
 *
 * References:
 *   • Huang, T.S. / Yang, G.J. / Tang, G.Y. (1979) "A Fast Two-
 *     Dimensional Median Filtering Algorithm", IEEE Trans. ASSP.
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/median
 */

import { mirrorIndex, validateRGBA } from './_util.js';

/** Slider range. */
export const MEDIAN_RADIUS_MIN = 0;
export const MEDIAN_RADIUS_MAX = 7;

/** Above this radius, switch to Huang 1979 histogram-median. */
const HUANG_THRESHOLD = 3;

/** Histogram bin count for Huang path. 256 ≈ 8-bit-equivalent. */
const HIST_BINS = 256;

/**
 * Default parameters. Identity (radius = 0).
 *
 * @returns {{ radius: number }}
 */
export function defaultParams() {
    return { radius: 0 };
}

/**
 * Identity test.
 *
 * @param {{ radius?: number }} params
 * @returns {boolean}
 */
export function isIdentityMedian(params) {
    if (!params) return true;
    const r = +params.radius;
    if (!Number.isFinite(r)) return true;
    return r <= 0;
}

/**
 * Per-pixel insertion sort + median pick for small radii. Returns the
 * median of `buf[0..len-1]` in-place (buf is destroyed).
 *
 * @param {Float32Array} buf
 * @param {number} len
 * @returns {number} median
 */
function insertionMedian(buf, len) {
    for (let i = 1; i < len; i++) {
        const v = buf[i];
        let j = i - 1;
        while (j >= 0 && buf[j] > v) {
            buf[j + 1] = buf[j];
            j--;
        }
        buf[j + 1] = v;
    }
    return buf[len >> 1];
}

/**
 * Apply median filter to a single planar channel using the small-radius
 * insertion-sort path. Writes to `dst`, reads `src`. `dst` must be
 * distinct from `src`.
 *
 * @param {Float32Array} src     length w·h
 * @param {Float32Array} dst     length w·h, distinct from src
 * @param {number} w
 * @param {number} h
 * @param {number} r
 */
function medianSmall(src, dst, w, h, r) {
    const K = 2 * r + 1;
    const winLen = K * K;
    const buf = new Float32Array(winLen);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let k = 0;
            for (let dy = -r; dy <= r; dy++) {
                const ny = mirrorIndex(y + dy, h);
                const rowOff = ny * w;
                for (let dx = -r; dx <= r; dx++) {
                    const nx = mirrorIndex(x + dx, w);
                    buf[k++] = src[rowOff + nx];
                }
            }
            dst[y * w + x] = insertionMedian(buf, winLen);
        }
    }
}

/**
 * Apply median filter via the Huang 1979 histogram-median for r > 3.
 *
 * The histogram is built ONCE per row at column 0, then slides one
 * column per pixel: remove the leftmost column's bins, add the
 * incoming rightmost column's bins. Median is found by scanning the
 * histogram from 0 to HIST_BINS until cumsum exceeds the target index
 * (winLen / 2).
 *
 * Input is Float32 so we quantise to HIST_BINS via min/max scan first.
 * This is the only acknowledged loss vs. exact-float median, and is
 * well below visible threshold for the user's radius range.
 *
 * @param {Float32Array} src     length w·h
 * @param {Float32Array} dst     length w·h, distinct from src
 * @param {number} w
 * @param {number} h
 * @param {number} r
 */
function medianHuang(src, dst, w, h, r) {
    const K = 2 * r + 1;
    const winLen = K * K;
    const mid = winLen >> 1;

    /* Quantise to HIST_BINS for histogram. We use the global min/max
     * of this channel so the bins span the full range present in the
     * channel — gives the histogram a meaningful resolution. */
    let mn = Infinity, mx = -Infinity;
    for (let i = 0; i < src.length; i++) {
        const v = src[i];
        if (v < mn) mn = v;
        if (v > mx) mx = v;
    }
    if (!Number.isFinite(mn) || !Number.isFinite(mx) || mx <= mn) {
        /* Degenerate channel — copy through. */
        dst.set(src);
        return;
    }
    const scale = (HIST_BINS - 1) / (mx - mn);
    const invScale = (mx - mn) / (HIST_BINS - 1);

    /* Quantised lookup: q[i] = round((src[i] - mn) * scale). */
    const q = new Uint16Array(src.length);
    for (let i = 0; i < src.length; i++) {
        const v = Math.round((src[i] - mn) * scale);
        q[i] = v < 0 ? 0 : v > (HIST_BINS - 1) ? (HIST_BINS - 1) : v;
    }

    const hist = new Int32Array(HIST_BINS);

    for (let y = 0; y < h; y++) {
        /* Reset histogram and load the initial window centered on x=0. */
        hist.fill(0);
        for (let dy = -r; dy <= r; dy++) {
            const ny = mirrorIndex(y + dy, h);
            const rowOff = ny * w;
            for (let dx = -r; dx <= r; dx++) {
                const nx = mirrorIndex(dx, w);
                hist[q[rowOff + nx]]++;
            }
        }
        /* Scan bins for the median of the initial window. */
        let cum = 0, b = 0;
        for (b = 0; b < HIST_BINS; b++) {
            cum += hist[b];
            if (cum > mid) break;
        }
        dst[y * w + 0] = b * invScale + mn;

        /* Slide right one column at a time. */
        for (let x = 1; x < w; x++) {
            /* Remove the leaving column at (x − r − 1). */
            const xOut = x - r - 1;
            for (let dy = -r; dy <= r; dy++) {
                const ny = mirrorIndex(y + dy, h);
                const nx = mirrorIndex(xOut, w);
                hist[q[ny * w + nx]]--;
            }
            /* Add the entering column at (x + r). */
            const xIn = x + r;
            for (let dy = -r; dy <= r; dy++) {
                const ny = mirrorIndex(y + dy, h);
                const nx = mirrorIndex(xIn, w);
                hist[q[ny * w + nx]]++;
            }
            /* Re-scan from 0 — Huang's original paper proposes a
             * "running median" optimisation that walks from the
             * previous median, but the worst-case is the same and
             * the from-0 scan is branch-predictor-friendly. */
            cum = 0;
            for (b = 0; b < HIST_BINS; b++) {
                cum += hist[b];
                if (cum > mid) break;
            }
            dst[y * w + x] = b * invScale + mn;
        }
    }
}

/**
 * Apply Median filter in-place on a packed RGBA Float32 buffer.
 *
 * Runs per-channel — R, G, B independently (alpha untouched). For
 * radius ≤ HUANG_THRESHOLD uses insertion-sort medianSmall; otherwise
 * medianHuang.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.radius  [0, 7]
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyMedian(rgba, w, h, params) {
    validateRGBA('applyMedian', rgba, w, h);
    const p = params || {};
    if (isIdentityMedian(p)) return rgba;

    const radius = Math.min(MEDIAN_RADIUS_MAX, Math.max(1, p.radius | 0));
    const N = w * h;
    const planar = new Float32Array(N);
    const filtered = new Float32Array(N);
    const impl = radius <= HUANG_THRESHOLD ? medianSmall : medianHuang;

    for (let c = 0; c < 3; c++) {
        for (let i = 0, j = c; i < N; i++, j += 4) planar[i] = rgba[j];
        impl(planar, filtered, w, h, radius);
        for (let i = 0, j = c; i < N; i++, j += 4) rgba[j] = filtered[i];
    }
    return rgba;
}
